import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../../firebase';

const AdminLogin = () => {
  const navigate = useNavigate();
  const idInputRef = useRef<HTMLInputElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [id, setId] = useState('');
  const [pw, setPw] = useState('');
  const [rememberId, setRememberId] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const savedId = localStorage.getItem('savedAdminId');
    if (savedId) {
      setId(savedId);
      setRememberId(true);
    }
    idInputRef.current?.focus();
  }, []);

  const handleLogin = async () => {
    setError('');
    setLoading(true);

    try {
      const email = `${id}@dwt.kr`;
      await signInWithEmailAndPassword(auth, email, pw);

      if (rememberId) {
        localStorage.setItem('savedAdminId', id);
      } else {
        localStorage.removeItem('savedAdminId');
      }

      navigate('/admin/dashboard');
    } catch (err: any) {
      console.error('Login failed:', err);
      setError('로그인에 실패했습니다. 아이디와 비밀번호를 확인해주세요.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleLogin();
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#f5f7fa',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          px: { xs: 3, sm: 5 },
          py: { xs: 4, sm: 6 },
          maxWidth: 400,
          width: '100%',
          textAlign: 'center',
        }}
      >
        <Box
          component="img"
          src="/assets/dwt_logo.png"
          alt="다원트레이드 로고"
          sx={{
            width: 120,
            mb: { xs: 1.5, sm: 2 },
            mx: 'auto',
          }}
        />

        <Typography
          variant={isMobile ? 'h6' : 'h5'}
          fontWeight="bold"
          mb={3}
        >
          다원트레이드 관리자 로그인
        </Typography>

        <TextField
          label="아이디"
          fullWidth
          value={id}
          onChange={(e) => setId(e.target.value)}
          margin="normal"
          autoComplete="off"
          inputRef={idInputRef}
          onKeyDown={handleKeyDown}
          size="small"
        />

        <TextField
          label="비밀번호"
          fullWidth
          type="password"
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          margin="normal"
          onKeyDown={handleKeyDown}
          size="small"
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={rememberId}
              onChange={(e) => setRememberId(e.target.checked)}
              color="primary"
            />
          }
          label="아이디 저장"
          sx={{
            mt: 1,
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            width: '100%',
          }}
        />

        {error && (
          <Typography
            color="error"
            variant="body2"
            sx={{ mt: 1, textAlign: 'left', width: '100%' }}
          >
            {error}
          </Typography>
        )}

        <Button
          variant="contained"
          fullWidth
          onClick={handleLogin}
          disabled={loading}
          sx={{
            mt: 3,
            py: 1.3,
            fontWeight: 600,
            fontSize: { xs: 15, sm: 16 },
          }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : '로그인'}
        </Button>
      </Paper>
    </Box>
  );
};

export default AdminLogin;
